import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Clock } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { INSIGHT_CATEGORIES } from "../data/content";
import { api } from "../api";

export default function Insights() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (search) params.set("q", search);
    setLoading(true);
    api.get(`/articles?${params.toString()}`)
      .then((r) => setItems(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, search]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <main className="pt-32 pb-24" data-testid="insights-page">
      <Seo title="Insights" description="Perspectives on social media, digital marketing, influencer marketing, branding, advertising and the creator economy from Trendsight Media, Delhi." />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="font-mono-accent text-xs uppercase tracking-[0.35em] text-blue-400 mb-5">
          Insights
        </motion.p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-50 leading-[1.02]" data-testid="insights-headline">
          SIGNAL, <span className="text-gradient">NOT NOISE.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
          Practical thinking on social media, performance marketing and the creator economy — from the team that ships it daily.
        </p>

        <div className="mt-10 flex flex-col lg:flex-row gap-5 lg:items-center">
          <div className="relative lg:w-80" data-testid="insights-search-wrap">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles..."
              data-testid="insights-search-input"
              className="w-full rounded-full bg-slate-900/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none pl-11 pr-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="insights-filters">
            <button
              onClick={() => setCategory("")}
              data-testid="insights-filter-all"
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-300 ${category === "" ? "bg-blue-600 text-white" : "border border-slate-700 text-slate-400 hover:text-blue-300 hover:border-blue-500/50"}`}
            >
              All
            </button>
            {INSIGHT_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(category === c ? "" : c)}
                data-testid={`insights-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-300 ${category === c ? "bg-blue-600 text-white" : "border border-slate-700 text-slate-400 hover:text-blue-300 hover:border-blue-500/50"}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-20 flex justify-center" data-testid="insights-loading">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.08}>
                <Link
                  to={`/insights/${a.slug}`}
                  data-testid={`article-card-${a.slug}`}
                  className="group flex flex-col h-full rounded-2xl border border-slate-800 bg-slate-900/60 overflow-hidden hover:border-blue-500/50 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
                >
                  <div className="h-40 bg-gradient-to-br from-blue-600/20 via-slate-900 to-cyan-500/12 relative flex items-end p-5">
                    <span className="font-mono-accent text-[10px] uppercase tracking-widest text-blue-300/90">{a.category}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h2 className="font-display text-lg font-bold text-slate-100 leading-snug group-hover:text-blue-300 transition-colors">{a.title}</h2>
                    <p className="text-[13px] text-slate-400 mt-2.5 leading-relaxed line-clamp-3">{a.summary}</p>
                    <div className="mt-auto pt-5 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                        <Clock size={12} /> {a.reading_time} min read
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-400 group-hover:text-blue-300 transition-colors">
                        Read Article <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
        {!loading && items.length === 0 && (
          <p className="mt-16 text-center text-slate-500" data-testid="insights-empty">No articles match your search yet.</p>
        )}
      </div>
    </main>
  );
}
